import BrandInlineCard from './cards/BrandInlineCard';
import CategoryInlineCard from './cards/CategoryInlineCard';
import BrandsInlineTable from './tables/BrandsInlineTable';
import BrandListInlineTable from './tables/BrandListInlineTable';
import Cat5sInlineTable from './tables/Cat5sInlineTable';
import CategoryListInlineTable from './tables/CategoryListInlineTable';
import RelatedCategoriesInlineTable from './tables/RelatedCategoriesInlineTable';
import HashtagsInlineTable from './tables/HashtagsInlineTable';
import RelatedSearchQueriesInlineTable from './tables/RelatedSearchQueriesInlineTable';
import CreatorsInlineTable from './tables/CreatorsInlineTable';
import PostsInlineList from './lists/PostsInlineList';
import RelatedPostsInlineList from './lists/RelatedPostsInlineList';
import {
    HashtagListCombined,
    HashtagListTrendLines,
    PopIndexListCombined,
    PopIndexListTrendLines,
} from './charts/listVisuals';
import {
    canExcludeRows,
    canonicalExcludeTerms,
    DEFAULT_EXCLUDE_MATCH,
    EXCLUDE_MATCH_MODES,
    formatExcludeTerm,
    hasPinnedList,
    isChartableListInput,
    isExcludableListInput,
    isPinnableListInput,
    isPinnedHashtagsInput,
    isStandaloneListInput,
    MAX_EXCLUDE_TERMS,
    MAX_TERM_LENGTH,
    selectsChartView,
} from './lib/listInputs';
import type {
    EntityDataInput,
    EntityDataOutput,
    TableRowModel,
    VisualProps,
    VisualWidget,
} from './types';

export {
    canExcludeRows,
    canonicalExcludeTerms,
    EXCLUDE_MATCH_MODES,
    formatExcludeTerm,
    isChartableListInput,
    isExcludableListInput,
    isPinnableListInput,
    isPinnedHashtagsInput,
    isStandaloneListInput,
    MAX_EXCLUDE_TERMS,
    selectsChartView,
};

const EntityListCombinedVisual: VisualWidget = {
    match: (input) => isStandaloneListInput(input) && input.aggregate === true,
    Component: PopIndexListCombined,
};

const EntityListTrendLinesVisual: VisualWidget = {
    match: (input) =>
        isStandaloneListInput(input) &&
        input.trend_lines === true &&
        !input.aggregate,
    Component: PopIndexListTrendLines,
};

const HashtagListCombinedVisual: VisualWidget = {
    match: (input) => isPinnedHashtagsInput(input) && input.aggregate === true,
    Component: HashtagListCombined,
};

const HashtagListTrendLinesVisual: VisualWidget = {
    match: (input) =>
        isPinnedHashtagsInput(input) &&
        input.trend_lines === true &&
        !input.aggregate,
    Component: HashtagListTrendLines,
};

/**
 * Ordered registry of inline visuals. Array order is the default precedence when
 * more than one widget matches. To add a widget: create its file under cards/ or
 * tables/ and add it here. See ./README.md for the full contract.
 */
const REGISTRY: VisualWidget[] = [
    BrandInlineCard,
    CategoryInlineCard,
    BrandsInlineTable,
    EntityListCombinedVisual,
    EntityListTrendLinesVisual,
    BrandListInlineTable,
    Cat5sInlineTable,
    CategoryListInlineTable,
    RelatedCategoriesInlineTable,
    HashtagListCombinedVisual,
    HashtagListTrendLinesVisual,
    HashtagsInlineTable,
    RelatedSearchQueriesInlineTable,
    CreatorsInlineTable,
    PostsInlineList,
    RelatedPostsInlineList,
];

export type ListView = 'table' | 'time_series' | 'combined';

/** The view a standalone list's args select. */
export const listViewOf = (input: EntityDataInput): ListView =>
    input?.aggregate
        ? 'combined'
        : input?.trend_lines
          ? 'time_series'
          : 'table';

/** The same args with the view flags set for `view`. */
export function withListView(
    args: EntityDataInput,
    view: ListView,
): EntityDataInput {
    const next = { ...args };
    delete next.aggregate;
    delete next.trend_lines;
    if (view === 'combined') next.aggregate = true;
    if (view === 'time_series') next.trend_lines = true;
    return next;
}

export function excludeRowFromArgs(
    args: EntityDataInput,
    row: { name?: string; excludeName?: string },
): EntityDataInput | null {
    const term = (row?.excludeName ?? row?.name ?? '').trim();
    if (!term || !canExcludeRows(args)) return null;
    const next = { ...args };

    if (isPinnedHashtagsInput(next)) {
        const lowered = term.toLowerCase();
        const kept = (next.hashtags ?? []).filter(
            (name) => name.toLowerCase() !== lowered,
        );
        // Emptying the pin list would silently make this a global top-N.
        if (!kept.length || kept.length === next.hashtags?.length) return null;
        next.hashtags = kept;
        return next;
    }

    const value = term.toLowerCase();
    // Code points, to match the backend's Python len().
    if (Array.from(value).length > MAX_TERM_LENGTH) return null;
    const existing = next.exclude ?? [];
    const match = DEFAULT_EXCLUDE_MATCH;
    if (existing.some((t) => t.match === match && t.value === value))
        return null;
    next.exclude = canonicalExcludeTerms([...existing, { match, value }]);
    return next;
}

/**
 * One row spliced out, to stand in until the re-run lands. `result.metrics` and
 * `_rows` are index-aligned, so they splice together or every row below the
 * removed one wears another row's badge. `_aggregate` /
 * `_combined_time_series` are left stale, which is safe only while the row ×
 * stays on tables — a chart config carries `aggregate`/`trend_lines`, so it is
 * a different query key. `applied_exclusions` is dropped rather than left
 * stale: it echoes the request that produced these rows, one term short of the
 * one being seeded, and the title falls back to the args without it.
 * Immutable: the input is the live cached payload.
 */
export function pruneRowFromOutput(
    output: EntityDataOutput | null | undefined,
    row: TableRowModel,
): EntityDataOutput | null {
    const index = row?.index;
    const result = output?.result;
    const metrics = result?.metrics;
    if (typeof index !== 'number' || !Number.isInteger(index)) return null;
    if (!Array.isArray(metrics) || index < 0 || index >= metrics.length) {
        return null;
    }

    const without = <T>(xs: T[]): T[] => [
        ...xs.slice(0, index),
        ...xs.slice(index + 1),
    ];
    const pinnedCount = result?.pinned_count;
    const rawRows = output?._rows;
    const { applied_exclusions: _staleEcho, ...keptResult } = result ?? {};
    return {
        ...output,
        _optimistic: true,
        result: {
            ...keptResult,
            metrics: without(metrics),
            ...(typeof pinnedCount === 'number' && pinnedCount > 0
                ? { pinned_count: pinnedCount - 1 }
                : {}),
        },
        ...(Array.isArray(rawRows) ? { _rows: without(rawRows) } : {}),
    };
}

/**
 * The list-config invariants, applied at the edit-modal save: view flags only
 * on a standalone list that has pins (what the backend accepts) and never both
 * (aggregate wins); a user `title` only on a pinnable list; `exclude` only on
 * a top-N list that isn't pinned (the backend 400s on the pair).
 */
export function normalizeListConfig(
    args: EntityDataInput,
    meta?: { title?: string } | null,
): { args: EntityDataInput; meta: { title?: string } } {
    const nextArgs = { ...args };
    if (!isChartableListInput(nextArgs)) {
        delete nextArgs.aggregate;
        delete nextArgs.trend_lines;
    }
    if (nextArgs.aggregate) delete nextArgs.trend_lines;
    if (!isExcludableListInput(nextArgs) || hasPinnedList(nextArgs)) {
        delete nextArgs.exclude;
    }
    const title = isPinnableListInput(nextArgs)
        ? meta?.title?.trim()
        : undefined;
    return {
        args: nextArgs,
        meta: title ? { title } : {},
    };
}

/** Loose shape for the Vercel AI SDK message parts we inspect. */
interface ToolPart {
    type?: string;
    state?: string;
    input?: EntityDataInput;
    output?: EntityDataOutput;
}

interface NavigationPart {
    data?: { entity_index?: number };
}

export interface InlineVisual {
    Component: VisualWidget['Component'];
    props: VisualProps;
}

export function selectInlineVisuals(
    parts: ToolPart[] | null | undefined,
    navigationPart: NavigationPart | null | undefined,
): InlineVisual[] {
    if (!Array.isArray(parts) || parts.length === 0) return [];
    if (!navigationPart) return [];

    const navIndex = navigationPart.data?.entity_index;
    const target = parts.find(
        (p) =>
            p.type === 'tool-get_entity_data' &&
            p.state === 'output-available' &&
            p.output?.success &&
            (Number.isInteger(navIndex)
                ? p.input?.entity_index === navIndex
                : p.input?.entity_index == null),
    );
    if (!target?.output) return [];

    const { output } = target;
    const input = resolveInputType(target.input ?? {}, output);
    return REGISTRY.filter((w) => w.match(input)).map((w) => ({
        Component: w.Component,
        props: { input, output },
    }));
}

export function resolveInputType(
    input: EntityDataInput,
    output: EntityDataOutput | undefined,
): EntityDataInput {
    if (input.entity_type || input.entity_index == null) return input;
    const type = output?._entity?.type;
    return type ? { ...input, entity_type: type } : input;
}

/** True if any registered widget can render a tool call with these inputs. */
export function hasVisualFor(
    input: EntityDataInput | null | undefined,
): boolean {
    if (!input) return false;
    return REGISTRY.some((w) => w.match(input));
}

/**
 * Resolve the inline visual for a tool result directly from `(input, output)`,
 * bypassing the chat's message-parts/navigation indirection. Used by the report
 * grid, which executes a stored widget config and renders the matching visual.
 * Returns the first matching widget (registry precedence) or null.
 */
export function selectVisualForToolResult(
    input: EntityDataInput | null | undefined,
    output: EntityDataOutput | null | undefined,
): InlineVisual | null {
    if (!input || !output?.success) return null;
    const widget = REGISTRY.find((w) => w.match(input));
    if (!widget) return null;
    return { Component: widget.Component, props: { input, output } };
}
